import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

import type { ApiEnvelope, FieldError } from '@/types/api';

const BASE_URL = import.meta.env['VITE_API_URL'] ?? 'http://localhost:4000/api/v1';

export class ApiRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    readonly fieldErrors: FieldError[] = [],
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }

  get byField(): Record<string, string> {
    return Object.fromEntries(this.fieldErrors.map((error) => [error.field, error.message]));
  }
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
  },
});

const ACCESS_KEY = 'searah.accessToken';
const REFRESH_KEY = 'searah.refreshToken';

function readStored(key: string): string | null {
  return window.sessionStorage.getItem(key) ?? window.localStorage.getItem(key);
}

let accessToken: string | null = readStored(ACCESS_KEY);

export interface SessionTokens {
  accessToken: string;
  refreshToken: string;
}

export function setAuthTokens(tokens: SessionTokens, remember: boolean): void {
  accessToken = tokens.accessToken;

  const target = remember ? window.localStorage : window.sessionStorage;
  const other = remember ? window.sessionStorage : window.localStorage;

  other.removeItem(ACCESS_KEY);
  other.removeItem(REFRESH_KEY);

  target.setItem(ACCESS_KEY, tokens.accessToken);
  target.setItem(REFRESH_KEY, tokens.refreshToken);
}

export function clearAuthTokens(): void {
  accessToken = null;

  for (const store of [window.sessionStorage, window.localStorage]) {
    store.removeItem(ACCESS_KEY);
    store.removeItem(REFRESH_KEY);
  }
}

export function getRefreshToken(): string | null {
  return readStored(REFRESH_KEY);
}

apiClient.interceptors.request.use((config) => {
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  return config;
});

type RetryableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

let refreshPromise: Promise<void> | null = null;

async function refreshSession(): Promise<void> {
  const remember = window.localStorage.getItem(REFRESH_KEY) !== null;

  refreshPromise ??= apiClient
    .post<ApiEnvelope<SessionTokens>>('/auth/refresh', { refreshToken: getRefreshToken() ?? undefined })
    .then((response) => {
      const tokens = response.data.data;
      if (tokens?.accessToken) setAuthTokens(tokens, remember);
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

function broadcastSessionExpiry(): void {
  clearAuthTokens();
  window.dispatchEvent(new CustomEvent('searah:session-expired'));
}

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,

  async (error: AxiosError<ApiEnvelope<null>>) => {
    const config = error.config as RetryableConfig | undefined;
    const status = error.response?.status;
    const envelope = error.response?.data;

    if (!error.response || !config) {
      throw new ApiRequestError(
        error.code === 'ECONNABORTED' ? 'The request timed out' : 'Cannot reach the server',
        0,
        'NETWORK_ERROR',
      );
    }

    const code = envelope?.meta?.code ?? 'ERROR';

    const isExpiredAccess = status === 401 && code === 'TOKEN_EXPIRED';
    const isAuthEndpoint = config.url?.includes('/auth/refresh') || config.url?.includes('/auth/login');

    if (isExpiredAccess && !config._retried && !isAuthEndpoint) {
      config._retried = true;

      try {
        await refreshSession();
        return await apiClient(config);
      } catch {
        broadcastSessionExpiry();
        throw new ApiRequestError('Your session has expired, please sign in again', 401, 'SESSION_EXPIRED');
      }
    }

    if (status === 401 && !isAuthEndpoint) {
      broadcastSessionExpiry();
    }

    throw new ApiRequestError(
      envelope?.message ?? error.message ?? 'Request failed',
      status ?? 0,
      code,
      envelope?.meta?.errors ?? [],
    );
  },
);

export async function apiGet<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const response = await apiClient.get<ApiEnvelope<T>>(url, { params });
  return response.data.data;
}

export async function apiGetEnvelope<T>(url: string, params?: Record<string, unknown>): Promise<ApiEnvelope<T>> {
  const response = await apiClient.get<ApiEnvelope<T>>(url, { params });
  return response.data;
}

export async function apiPost<T>(url: string, body?: unknown): Promise<T> {
  const response = await apiClient.post<ApiEnvelope<T>>(url, body);
  return response.data.data;
}

export async function apiPatch<T>(url: string, body?: unknown): Promise<T> {
  const response = await apiClient.patch<ApiEnvelope<T>>(url, body);
  return response.data.data;
}

export async function apiPut<T>(url: string, body?: unknown): Promise<T> {
  const response = await apiClient.put<ApiEnvelope<T>>(url, body);
  return response.data.data;
}

export async function apiDelete<T = null>(url: string): Promise<T> {
  const response = await apiClient.delete<ApiEnvelope<T>>(url);
  return response.data.data;
}
